import { projectApi } from '@/api/projectApi'
import { chapterCompletionMessageKey } from '@/utils/chapterGenerate'
import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'
import { AlertTriangle, CircleStop, LoaderCircle, RefreshCw } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { chapterApi } from '@/api/chapterApi'
import { friendlyMessage } from '@/api/http'
import { Button } from '@/components/ui/button'
import { ChapterQualityCard } from '@/components/quality/ChapterQualityCard'
import { parseChapterQualitySnapshot, type ChapterQualitySnapshot } from '@/components/quality/qualityGate'
import type { Asset } from '@/types/nf'
import { useUiStore, type GeneratingChapter } from '@/stores/uiStore'
import { formatDuration, formatNumber } from '@/utils/format'
import { finalizeGeneratedChapterAfterDone, type ChapterWordCountEstimate } from '@/utils/chapterGenerate'

interface ChapterStreamViewProps {
  draft: GeneratingChapter
  userId?: string
}

interface ChunkPayload {
  delta: string
}

interface DonePayload {
  chapter_id: string
  title: string
  word_count: number
  provider: string
  duration_ms: number
  word_count_estimate?: ChapterWordCountEstimate
}

interface StreamErrorState {
  code: string
  category: string
  message: string
  retryable: boolean
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function isChunkPayload(value: unknown): value is ChunkPayload {
  return isObject(value) && typeof value.delta === 'string'
}

function isWordCountEstimate(value: unknown): value is ChapterWordCountEstimate {
  return (
    isObject(value) &&
    typeof value.target === 'number' &&
    typeof value.min === 'number' &&
    typeof value.max === 'number' &&
    value.target >= 1 &&
    value.min >= 0 &&
    value.max >= value.min
  )
}

export function isDonePayload(value: unknown): value is DonePayload {
  return (
    isObject(value) &&
    typeof value.chapter_id === 'string' &&
    typeof value.title === 'string' &&
    typeof value.word_count === 'number' &&
    typeof value.provider === 'string' &&
    typeof value.duration_ms === 'number' &&
    (value.word_count_estimate === undefined || isWordCountEstimate(value.word_count_estimate))
  )
}

function errorStateFromPayload(value: unknown, fallback: string): StreamErrorState {
  if (isObject(value)) {
    const message =
      typeof value.message === 'string'
        ? value.message
        : typeof value.error === 'string'
          ? friendlyMessage(new Error(value.error), fallback)
          : fallback
    return {
      code: typeof value.code === 'string' ? value.code : 'generation_failed',
      category: typeof value.category === 'string' ? value.category : 'unknown',
      message,
      retryable: typeof value.retryable === 'boolean' ? value.retryable : true,
    }
  }
  return {
    code: 'generation_failed',
    category: 'unknown',
    message: friendlyMessage(new Error(String(value)), fallback),
    retryable: true,
  }
}

export function ChapterStreamView({ draft, userId }: ChapterStreamViewProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const selectChapter = useUiStore((s) => s.selectChapter)
  const clearGeneratingChapter = useUiStore((s) => s.clearGeneratingChapter)
  const setGeneratingAbort = useUiStore((s) => s.setGeneratingAbort)
  const [content, setContent] = useState('')
  const [error, setError] = useState<StreamErrorState | null>(null)
  const [isStreaming, setIsStreaming] = useState(true)
  const [reconnectAttempt, setReconnectAttempt] = useState(0)
  const [quality, setQuality] = useState<ChapterQualitySnapshot | null>(null)
  const [qualityHistory, setQualityHistory] = useState<ChapterQualitySnapshot[]>([])
  const closeRef = useRef<(() => void) | null>(null)
  const startedRef = useRef(false)
  const pendingChunks = useRef<string[]>([])
  const rafId = useRef<number | null>(null)
  const streamRef = useRef<HTMLDivElement | null>(null)
  const retryRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (startedRef.current) return

    const flush = () => {
      rafId.current = null
      if (pendingChunks.current.length === 0) return
      const next = pendingChunks.current.join('')
      pendingChunks.current = []
      setContent((prev) => prev + next)
    }

    const scheduleFlush = () => {
      if (rafId.current !== null) return
      rafId.current = requestAnimationFrame(flush)
    }

    const flushNow = () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current)
        rafId.current = null
      }
      flush()
    }

    const stopStream = () => {
      closeRef.current?.()
      closeRef.current = null
      setGeneratingAbort(null)
    }

    const startStream = () => {
      startedRef.current = true
      setIsStreaming(true)
      setError(null)
      setReconnectAttempt(0)
      closeRef.current = chapterApi.generateStream(
        {
          project_id: draft.projectId,
          outline: draft.outline,
          chapter_number: draft.chapterNumber,
          title: draft.title || undefined,
          chapter_mode: draft.chapterMode,
          target_words: draft.targetWords,
          lang: draft.lang,
        },
        {
          onEvent: (event, data) => {
            if (event === 'chunk') {
              setIsStreaming(true)
              if (isChunkPayload(data)) {
                pendingChunks.current.push(data.delta)
                scheduleFlush()
              }
              return
            }

            if (event === 'done') {
              flushNow()
              stopStream()
              if (!isDonePayload(data)) {
                setError({
                  code: 'invalid_done_payload',
                  category: 'bad_response',
                  message: t('chapter.generate.invalidDonePayload'),
                  retryable: true,
                })
                toast.error(t('chapter.generate.invalidDonePayload'))
                clearGeneratingChapter()
                return
              }
              setIsStreaming(false)
              clearGeneratingChapter()
              void (async () => {
                const queryKey = ['chapters', userId, draft.projectId, draft.branchId]
                await finalizeGeneratedChapterAfterDone({
                  queryKey,
                  done: data,
                  projectId: draft.projectId,
                  branchId: draft.branchId,
                  invalidateQueries: (key) => queryClient.invalidateQueries({ queryKey: key }),
                  refetchQueries: (key) => queryClient.refetchQueries({ queryKey: key, type: 'active' }),
                  selectChapter,
                  clearGeneratingChapter,
                })
                // #415: done 兜底刷新卷章树评分源(评分可能在 done 后异步落库)。
                void queryClient.invalidateQueries({ queryKey: ['project-assets', draft.projectId] })
                toast.success(
                  t('chapter.generate.doneSummary', {
                    title: data.title || t('chapter.untitled'),
                    words: formatNumber(data.word_count),
                    provider: data.provider,
                    duration: formatDuration(data.duration_ms),
                  }),
                )
                const policy = await projectApi.getCascadePolicy(draft.projectId).catch(() => undefined)
                toast.message(t(chapterCompletionMessageKey(policy)))
              })().catch(() => {
                toast.warning(t('chapter.generate.doneRefreshFailed'))
              })
              return
            }

            if (event === 'quality') {
              const nextQuality = parseChapterQualitySnapshot(data)
              if (!nextQuality) return
              setQuality(nextQuality)
              setQualityHistory((prev) => [...prev.filter((item) => item.id !== nextQuality.id), nextQuality])
              queryClient.setQueryData<ChapterQualitySnapshot[]>(
                ['chapter-quality-history', nextQuality.chapter_id],
                (prev = []) => [...prev.filter((item) => item.id !== nextQuality.id), nextQuality],
              )
              // #415 方案A: SSE quality 事件已携带完整评分, 直接注入 project-assets 缓存(卷章树评分数据源),
              // 不依赖 invalidate→refetch(避免"评分 commit vs refetch 到达"的时序竞态/SQLite WAL 读窗口 →
              // refetch 拿到无评分中间态)。卷章树评分取自 chapter_text asset 的 payload.quality。
              queryClient.setQueryData<Asset[]>(
                ['project-assets', draft.projectId, draft.branchId],
                (old) => {
                  if (!old) return old // 缓存未建立 → 由下方 invalidate 兜底 refetch
                  return old.map((asset) => {
                    if (asset.chapter_id !== nextQuality.chapter_id || asset.asset_type !== 'chapter_text') return asset
                    let payloadObj: Record<string, unknown>
                    try {
                      payloadObj = JSON.parse(asset.payload) as Record<string, unknown>
                    } catch {
                      return asset
                    }
                    const prevQuality =
                      payloadObj.quality && typeof payloadObj.quality === 'object'
                        ? (payloadObj.quality as Record<string, unknown>)
                        : {}
                    payloadObj.quality = {
                      ...prevQuality,
                      score: nextQuality.score,
                      status: nextQuality.status,
                      grade: nextQuality.grade,
                      layer: nextQuality.layer,
                    }
                    return { ...asset, payload: JSON.stringify(payloadObj) }
                  })
                },
              )
              // 兜底: 精确 key 未命中(分支归一化差异)或缓存不存在时, 前缀 invalidate 后台刷新拿权威数据。
              void queryClient.invalidateQueries({ queryKey: ['project-assets', draft.projectId] })
              return
            }

            if (event === 'error') {
              const nextError = errorStateFromPayload(data, t('common.serviceError'))
              setError(nextError)
              setIsStreaming(false)
              toast.error(nextError.message)
              stopStream()
            }
          },
          onError: (err) => {
            const nextError = {
              code: 'stream_reconnect_failed',
              category: 'network',
              message: friendlyMessage(err, t('chapter.generate.reconnectFailed')),
              retryable: true,
            }
            setError(nextError)
            setIsStreaming(false)
            toast.error(nextError.message)
            stopStream()
          },
          onOpen: () => {
            setIsStreaming(true)
            setReconnectAttempt(0)
          },
          onReconnect: (attempt) => {
            setReconnectAttempt(attempt)
            setIsStreaming(true)
          },
        },
      )

      setGeneratingAbort(() => {
        stopStream()
        clearGeneratingChapter()
      })
    }

    retryRef.current = () => {
      closeRef.current?.()
      closeRef.current = null
      pendingChunks.current = []
      setContent('')
      startStream()
    }

    const startTimer = window.setTimeout(() => {
      startStream()
    }, 0)

    return () => {
      window.clearTimeout(startTimer)
      const hadActiveStream = closeRef.current !== null
      if (rafId.current !== null) cancelAnimationFrame(rafId.current)
      closeRef.current?.()
      closeRef.current = null
      retryRef.current = null
      setGeneratingAbort(null)
      if (hadActiveStream) useUiStore.getState().clearGeneratingChapter()
    }
    // Generation is non-idempotent: this component instance must issue exactly one POST.
  }, [])

  useEffect(() => {
    streamRef.current?.scrollTo({ top: streamRef.current.scrollHeight })
  }, [content])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-3 flex items-start justify-between gap-3 rounded-md border bg-background/45 p-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium">
            {isStreaming ? (
              <LoaderCircle className="size-4 animate-spin text-primary" />
            ) : (
              <AlertTriangle className="size-4 text-destructive" />
            )}
            {isStreaming
              ? t('chapter.generate.generatingChapter', { value: draft.chapterNumber })
              : t('chapter.generate.failedTitle', { value: draft.chapterNumber })}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {reconnectAttempt > 0
              ? t('chapter.generate.reconnecting', { value: reconnectAttempt })
              : t('chapter.generate.idempotentRetryHint')}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            if (!window.confirm(t('chapter.generate.abortConfirm'))) return
            closeRef.current?.()
            clearGeneratingChapter()
          }}
        >
          <CircleStop className="size-4" />
          {t('chapter.generate.abort')}
        </Button>
      </div>

      {error ? (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="font-medium">{error.message}</div>
              <div className="mt-1 text-xs text-destructive/80">
                {t('chapter.generate.errorMeta', {
                  code: error.code,
                  category: error.category,
                })}
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => retryRef.current?.()}
              disabled={!error.retryable}
            >
              <RefreshCw className="size-4" />
              {t('chapter.generate.retry')}
            </Button>
          </div>
        </div>
      ) : null}

      {quality ? (
        <div className="mb-3">
          <ChapterQualityCard quality={quality} history={qualityHistory} compact />
        </div>
      ) : null}

      <div ref={streamRef} className="min-h-0 flex-1 overflow-auto rounded-md border bg-background/40 p-4">
        {content ? (
          <article className="space-y-4 text-sm leading-7 text-foreground">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
              {content}
            </ReactMarkdown>
          </article>
        ) : (
          <div className="grid min-h-72 place-items-center text-sm text-muted-foreground">
            {t('chapter.generate.waitingForStream')}
          </div>
        )}
      </div>
    </div>
  )
}
